import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "shared/Card";
import styles from "./SpeakerV2InfoForm.module.css";
import InputItem from "Events/Components/InputItem";
import AddButton from "Events/Components/AddButton";
import classNames from "classnames";
import AddSpeakerModal from "Events/Components/AddSpeakerModal";
import SelectPhotoModal from "../SelectPhotoModal";
import { Plus } from "react-ikonate";

export function SpeakerV2InfoForm({
  publishingInfo,
  pageMode,
  onSpeakerChange,
  onCaptionChange,
  onDisplayImageChange,
}) {
  const [showAddSpeakerModal, setShowAddSpeakerModal] = useState(false);
  const [primaryCaptionText, setprimaryCaptionText] = useState(
    publishingInfo?.marketing?.primaryCaption?.saved ||
      publishingInfo?.marketing?.primaryCaption?.value ||
      ""
  );
  const [secondaryCaptionText, setSecondaryCaptionText] = useState(
    publishingInfo?.marketing?.secondaryCaption?.saved ||
      publishingInfo?.marketing?.secondaryCaption?.value ||
      ""
  );

  const [displayImage, setDisplayImage] = useState(
    publishingInfo?.marketing?.displayImage?.saved ||
      publishingInfo?.marketing?.displayImage?.value ||
      ""
  );

  const [errors, setErrors] = useState({
    image: "",
  });

  useEffect(() => {
    const newPrimaryCaption =
      publishingInfo?.marketing?.primaryCaption?.saved ||
      publishingInfo?.marketing?.primaryCaption?.value ||
      "";

    const newSecondaryCaption =
      publishingInfo?.marketing?.secondaryCaption?.saved ||
      publishingInfo?.marketing?.secondaryCaption?.value ||
      "";

    const newDisplayImage =
      publishingInfo?.marketing?.displayImage?.saved ||
      publishingInfo?.marketing?.displayImage?.value ||
      "";

    setprimaryCaptionText(decodeURI(newPrimaryCaption));
    setSecondaryCaptionText(decodeURI(newSecondaryCaption));
    setDisplayImage(newDisplayImage);
  }, [publishingInfo]);

  const [isRequired, setIsRequired] = useState({
    visibleTo: false,
    displayImage: false,
  });

  useEffect(() => {
    const updatedIsRequired = { ...isRequired };

    if (publishingInfo?.marketing?.visibleTo?.required !== undefined) {
      updatedIsRequired.visibleTo =
        publishingInfo?.marketing?.visibleTo?.required;
    }
    if (publishingInfo?.marketing?.displayImage?.required !== undefined) {
      updatedIsRequired.displayImage =
        publishingInfo?.marketing?.displayImage?.required;
    }

    setIsRequired(updatedIsRequired);
  }, [publishingInfo]);

  const handleAddSpeaker = (speakersToAdd) => {
    const currentSpeakers = publishingInfo.speakers?.saved
      ? publishingInfo.speakers.saved
      : publishingInfo.speakers?.value || [];

    const updatedSpeakers = [
      ...currentSpeakers,
      ...speakersToAdd.filter(
        (newSpeaker) =>
          !currentSpeakers.some(
            (existingSpeaker) => existingSpeaker.wwgId === newSpeaker.wwgId
          )
      ),
    ];

    if (
      primaryCaptionText.trim() === "" &&
      secondaryCaptionText.trim() === ""
    ) {
      const toPascalCase = (str) =>
        str
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

      const parsedSpeakers = updatedSpeakers.map((s) => {
        let cleanedName = s.name?.trim().replace(/\s+/g, " ") || "";

        let first = "";
        let last = "";

        if (cleanedName.includes(",")) {
          const [l, f] = cleanedName.split(",").map((p) => p.trim());
          first = toPascalCase(f || "");
          last = toPascalCase(l || "");
        } else {
          const parts = cleanedName.split(" ");
          first = toPascalCase(parts[0] || "");
          last = toPascalCase(parts.slice(1).join(" ") || "");
        }

        return { first, last };
      });

      let primary = "";
      let secondary = "";

      if (parsedSpeakers.length === 1) {
        primary = parsedSpeakers[0].first || "";
        secondary = parsedSpeakers[0].last || "";
      } else if (
        parsedSpeakers.length > 1 &&
        parsedSpeakers[0].last &&
        parsedSpeakers[0].last.toLowerCase() ===
          parsedSpeakers[1].last.toLowerCase()
      ) {
        primary = `${parsedSpeakers[0].first} & ${parsedSpeakers[1].first}`;
        secondary = parsedSpeakers[0].last;
      } else {
        primary = parsedSpeakers[0].first;
        secondary = parsedSpeakers[0].last;
      }

      setprimaryCaptionText(primary);
      setSecondaryCaptionText(secondary);

      onCaptionChange(
        {
          "marketing.primary": primary,
          "marketing.secondary": secondary,
        },
        null,
        updatedSpeakers
      );
    } else {
      onSpeakerChange(updatedSpeakers);
    }
  };

  const handleRemoveSpeaker = (speakerToRemove) => {
    const currentSpeakers = publishingInfo.speakers?.saved
      ? publishingInfo.speakers.saved
      : publishingInfo.speakers?.value || [];

    const updatedSpeakers = currentSpeakers.filter(
      (speaker) => speaker.wwgId !== speakerToRemove.wwgId
    );

    // Always recalculate captions using the same logic as handleAddSpeaker
    if (updatedSpeakers.length === 0) {
      setprimaryCaptionText("");
      setSecondaryCaptionText("");
      onCaptionChange(
        {
          "marketing.primary": "",
          "marketing.secondary": "",
        },
        null,
        updatedSpeakers
      );
      return;
    }

    const toPascalCase = (str) =>
      str
        .toLowerCase()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

    const parsedSpeakers = updatedSpeakers.map((s) => {
      const cleanedName = s.name?.trim().replace(/\s+/g, " ") || "";
      const [first, ...rest] = cleanedName.split(" ");
      return {
        first: toPascalCase(first),
        last: toPascalCase(rest.join(" ")),
      };
    });

    let primary = parsedSpeakers[0].first;
    let secondary = parsedSpeakers[0].last;

    if (
      parsedSpeakers.length > 1 &&
      parsedSpeakers[0].last &&
      parsedSpeakers[0].last.toLowerCase() ===
        parsedSpeakers[1].last.toLowerCase()
    ) {
      primary = `${parsedSpeakers[0].first} & ${parsedSpeakers[1].first}`;
      secondary = parsedSpeakers[0].last;
    }

    setprimaryCaptionText(primary);
    setSecondaryCaptionText(secondary);

    onCaptionChange(
      {
        "marketing.primary": primary,
        "marketing.secondary": secondary,
      },
      null,
      updatedSpeakers
    );
  };

  const handleCaptionsInputChange = (dataType, event) => {
    let newValue = event.target.value;

    if (dataType === "marketing.primary") {
      setprimaryCaptionText(newValue);
    } else if (dataType === "marketing.secondary")
      setSecondaryCaptionText(newValue);

    if (onCaptionChange) {
      const normalized =
        newValue === "" ? null : newValue.replace(/↵/g, "\r\n").trim();
      onCaptionChange(dataType, normalized);
    }
  };

  // const handleImageChange = (selectedImage) => {
  //   if (selectedImage) {
  //     const reader = new FileReader();
  //     reader.onload = (event) => {
  //       const base64ImageData = event.target.result;
  //       setDisplayImage(base64ImageData);

  //       if (onDisplayImageChange) {
  //         onDisplayImageChange({
  //           path: "marketing.displayImage",
  //           value: base64ImageData,
  //         });
  //       }

  //       //Clear error if user uploaded an image
  //       if (errors.image) {
  //         setErrors({ ...errors, image: "" });
  //       }
  //     };
  //     reader.readAsDataURL(selectedImage);
  //   } else {
  //     setDisplayImage(null);
  //   }
  // };

  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [currentPhotoSrc, setCurrentPhotoSrc] = useState(displayImage);

  useEffect(() => {
    if (publishingInfo?.marketing?.displayImage?.saved) {
      setCurrentPhotoSrc(publishingInfo?.marketing.displayImage.saved);
    } else if (publishingInfo?.marketing?.displayImage?.value) {
      setCurrentPhotoSrc(publishingInfo?.marketing.displayImage.value);
    }
  }, [publishingInfo?.marketing]);

  const handleSubmitPhoto = (photoUrl) => {
    setCurrentPhotoSrc(photoUrl);
    setDisplayImage(photoUrl);

    if (onDisplayImageChange) {
      onDisplayImageChange({
        path: "marketing.displayImage",
        value: photoUrl,
      });
    }

    if (errors?.image) {
      setErrors({ ...errors, image: "" });
    }

    setShowPhotoModal(false);
  };

  const renderAddSpeaker = () => {
    const speakerPermission =
      pageMode === "view" ? "read" : publishingInfo?.speakers?.permission;

    const speakerValue = publishingInfo?.speakers?.value;
    const speakerSaved = publishingInfo?.speakers?.saved;

    const speakersToDisplay = speakerSaved ? speakerSaved : speakerValue || [];

    if (speakerPermission === "write" || speakerPermission === undefined) {
      if (speakersToDisplay) {
        return (
          <>
            <div className={styles.speakerList}>
              {speakersToDisplay.map((speaker, index) => (
                <div className={styles.speakerItem} key={index}>
                  <div>
                    {speaker?.name ||
                      speaker?.iboNumber ||
                      "No Name For Speaker"}
                  </div>
                  <div
                    className={styles.removeSpeaker}
                    onClick={() => handleRemoveSpeaker(speaker)}
                  >
                    &times;
                  </div>
                </div>
              ))}
            </div>
            <div
              className={classNames(styles["button-container"])}
              onClick={() => setShowAddSpeakerModal(true)}
            >
              <AddButton description={"Add Speaker"} color="#2A3847" />
            </div>
          </>
        );
      } else {
        return (
          <div
            className={classNames(styles["button-container"])}
            onClick={() => setShowAddSpeakerModal(true)}
          >
            <AddButton description={"Add Speaker"} color="#2A3847" />
          </div>
        );
      }
    } else if (speakerPermission === "read") {
      return (
        <span className={styles.textValue}>
          {speakersToDisplay.length > 0
            ? speakersToDisplay.map((speaker, index) => (
                <span key={index}>
                  {speaker?.name || speaker?.iboNumber || "No Name For Speaker"}
                  {index < speakersToDisplay.length - 1 ? "," : ""}
                </span>
              ))
            : "N/A"}
        </span>
      );
    } else if (speakerPermission === "hidden") {
      return null;
    }
  };

  const renderCaptionInput = (dataType, label, onChange, onBlur) => {
    let displayValue = "";
    if (dataType === "marketing.primary") {
      displayValue = primaryCaptionText;
    } else if (dataType === "marketing.secondary") {
      displayValue = secondaryCaptionText;
    }

    const permission = (() => {
      if (dataType === "marketing.primary") {
        return publishingInfo?.marketing?.primaryCaption?.permission;
      }
      if (dataType === "marketing.secondary") {
        return publishingInfo?.marketing?.secondaryCaption?.permission;
      }
      return "hidden";
    })();

    if (permission === "hidden") {
      return null;
    }

    if (permission !== "write" || pageMode === "view") {
      return (
        <div className={styles.captionInputWrapper}>
          <span className={styles.viewLabel}>{label}</span>
          <span className={styles.textInput}>{displayValue || "N/A"}</span>
        </div>
      );
    }

    return (
      <div className={styles.captionInputWrapper}>
        <span className={styles.captionLabel}>{label}</span>
        <InputItem
          value={displayValue}
          onChange={(event) => onChange(event)}
          className={styles.captionInput}
          removeLabel={true}
          placeholder="Click to type"
        />
      </div>
    );
  };

  const renderDisplayImage = (displayValue, label) => {
    // Should only register on Local v2.0
    if (publishingInfo?.event?.ruleset?.value !== "Local v2.0") {
      return null;
    }

    const permission =
      pageMode === "view"
        ? "read"
        : publishingInfo?.marketing?.displayImage?.permission;

    const imageSrc = permission === "write" ? displayImage : displayValue;

    // Render the ImagePicker if permission is "write"
    if (permission === "write" || permission === undefined) {
      // return (
      //   <div className={styles.displayImage}>
      //     <div className={styles.col}>
      //       <span className={styles.imageLabel}>
      //         Speaker Photo{" "}
      //         {isRequired.displayImage && pageMode == "edit" ? " *" : ""}
      //       </span>
      //       <div className={styles.iconContainer}>
      //         <ImagePicker
      //           changeImageItem={handleImageChange}
      //           isPublish={true}
      //           imgSrc={displayImage}
      //           className={styles.icon}
      //         />
      //       </div>
      //     </div>
      //   </div>
      //);
      return (
        <div className={styles.displayImage}>
          <div className={styles.col}>
            <span className={styles.imageLabel}>
              Speaker Photo{" "}
              {isRequired.displayImage && pageMode === "edit" ? " *" : ""}
            </span>

            <div className={styles.photoWrapper}>
              {currentPhotoSrc ? (
                <img
                  src={currentPhotoSrc}
                  alt="Selected"
                  className={styles.icon}
                  onClick={() => setShowPhotoModal(true)}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setShowPhotoModal(true)}
                  className={classNames(styles.imagePickerUploadButton)}
                >
                  <Plus className={styles.imagePickerPlus} borderwidth={5} />
                </button>
              )}
            </div>
          </div>

          {showPhotoModal && (
            <SelectPhotoModal
              isVisible={showPhotoModal}
              setIsVisible={setShowPhotoModal}
              onSubmit={handleSubmitPhoto}
              folder="speakers"
            />
          )}
        </div>
      );
    }

    // Render the label and non-editable display image if permission is "read"
    if (permission === "read") {
      return (
        <div className={styles.displayImage}>
          <div className={styles.col}>
            <span className={styles.imageLabel}>{label}</span>
            {imageSrc ? (
              <img
                src={imageSrc}
                alt="Display"
                className={styles.displayImagePreview}
              />
            ) : (
              <span className={styles.textValue}>N/A</span>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Card
      children={
        <section className={styles.container}>
          <div className={styles.title}>Speaker Information</div>

          <hr className={styles.hr} />

          <div className={classNames(styles["input-container"])}>
            <div className={classNames(styles.flex)}>
              <span className={styles.label}>Speakers</span>
              {renderAddSpeaker()}
            </div>
          </div>

          {renderCaptionInput(
            "marketing.primary",
            `Display First Name(s)${
              publishingInfo?.marketing?.primaryCaption?.required &&
              pageMode === "edit"
                ? " *"
                : ""
            }`,
            (event) => handleCaptionsInputChange("marketing.primary", event)
          )}

          {renderCaptionInput(
            "marketing.secondary",
            `Display Last Name(s)${
              publishingInfo?.marketing?.secondaryCaption?.required &&
              pageMode === "edit"
                ? " *"
                : ""
            }`,
            (event) => handleCaptionsInputChange("marketing.secondary", event)
          )}

          {renderDisplayImage(
            publishingInfo?.marketing?.displayImage?.saved ||
              publishingInfo?.marketing?.displayImage?.value,
            "Display Image"
          )}

          <AddSpeakerModal
            isOpen={showAddSpeakerModal}
            onCollapse={() => setShowAddSpeakerModal(false)}
            onClick={(speakers) => handleAddSpeaker(speakers)}
          />
        </section>
      }
    />
  );
}

SpeakerV2InfoForm.propTypes = {
  visibleOnDate: PropTypes.string,
  setVisibleOnDate: PropTypes.func,
  visibleOnTime: PropTypes.string,
  setVisibleOnTime: PropTypes.func,
  startFeeDate: PropTypes.string,
  setStartFeeDate: PropTypes.func,
  startFeeTime: PropTypes.string,
  setStartFeeTime: PropTypes.func,
  changeableUntilDate: PropTypes.string,
  setChangeableUntilDate: PropTypes.func,
  changeableUntilTime: PropTypes.string,
  setChangeableUntilTime: PropTypes.func,
  purchaseBeginDate: PropTypes.string,
  setPurchaseBeginDate: PropTypes.func,
  purchaseBeginTime: PropTypes.string,
  setPurchaseBeginTime: PropTypes.func,
  purchaseEndTime: PropTypes.string,
  setPurchaseEndTime: PropTypes.func,
  purchaseEndDate: PropTypes.string,
  setPurchaseEndDate: PropTypes.func,
  visibleTo: PropTypes.string,
  setVisibleTo: PropTypes.func,
};
